<template>
    <b-modal id="editPatientModal" v-model="showModal" title="Edit Patient Information" size="xl"
        :no-close-on-backdrop="isSubmitting" :no-close-on-esc="isSubmitting" @hidden="resetForm">
        <form ref="form" @submit.prevent="updatePatient">
            <div class="row g-3">
                <div class="col-md-6">
                    <h6 class="mb-3 text-primary">Basic Information</h6>
                    <div class="form-group mb-3">
                        <label class="mb-0 form-label">First & Last Name <span class="text-danger">*</span></label>
                        <input v-model.trim="fullName" type="text" class="form-control" placeholder="Enter full name" />
                    </div>
                    <div class="row">
                        <div class="col-6">
                            <div class="form-group mb-3">
                                <label class="mb-0 form-label">Gender <span class="text-danger">*</span></label>
                                <select v-model.number="gender" class="form-select">
                                    <option :value="null" disabled>Select gender</option>
                                    <option :value="1">Male</option>
                                    <option :value="2">Female</option>
                                </select>
                            </div>
                        </div>
                        <div class="col-6">
                            <div class="form-group mb-3" v-if="useAgeOrDob === 'age'">
                                <label class="mb-0 form-label"><input type="radio" v-model="useAgeOrDob" value="age" />
                                    Age <span class="text-danger">*</span> <input type="radio" v-model="useAgeOrDob"
                                        value="dob" class="ms-5" /> Date of Birth <span
                                        class="text-danger">*</span></label>
                                <input v-model.number="age" type="number" min="0" class="form-control"
                                    placeholder="Enter age" />
                            </div>
                            <div class="form-group mb-3" v-else>
                                <label class="mb-0 form-label"><input type="radio" v-model="useAgeOrDob" value="age" />
                                    Age <span class="text-danger">*</span> <input type="radio" v-model="useAgeOrDob"
                                        value="dob" class="ms-5" /> Date of Birth <span
                                        class="text-danger">*</span></label>
                                <input v-model="dateOfBirth" type="date" class="form-control" />
                            </div>
                        </div>
                    </div>
                    <div class="form-group mb-3">
                        <label class="mb-0 form-label">Marital Status</label>
                        <select v-model="maritalStatus" class="form-select">
                            <option value="">Select status</option>
                            <option value="Single">Single</option>
                            <option value="Married">Married</option>
                            <option value="Separated">Separated</option>
                            <option value="Divorced">Divorced</option>
                            <option value="Widowed">Widowed</option>
                        </select>
                    </div>
                    <div class="form-group mb-3">
                        <label class="mb-0 form-label">Address</label>
                        <input v-model.trim="address" type="text" class="form-control" placeholder="Enter address" />
                    </div>

                    <div class="form-group mt-3">
                        <label class="mb-0 form-label">Next of Kin</label>
                        <input v-model.trim="nok" type="text" class="form-control" placeholder="Next of kin name" />
                    </div>
                    <div class="form-group mb-0 mt-3">
                        <label class="mb-0 form-label">Next of Kin Contact</label>
                        <input v-model.trim="nok_contact" type="text" class="form-control"
                            placeholder="Next of kin contact" />
                    </div>
                </div>
                <div class="col-md-6">
                    <h6 class="mb-3 text-primary">Background</h6>
                    <div class="form-group mb-3">
                        <label class="mb-0 form-label">Nationality</label>
                        <multiselect v-model="nationality" :options="countries" label="name" track-by="name"
                            :show-labels="false" placeholder="Select nationality" />
                    </div>
                    <div class="form-group mb-3">
                        <label class="mb-0 form-label">Residential Status</label>
                        <select v-model="residential_status" @input="convertResidentialStatus" class="form-select">
                            <option value="">Select option</option>
                            <option value="Resident">Resident</option>
                            <option value="Internally Displaced">Internally Displaced</option>
                            <option value="Refugee">Refugee</option>
                            <option value="Migrant">Migrant</option>
                        </select>
                    </div>
                    <div class="row">
                        <div class="col-6">
                            <div class="form-group mb-3">
                                <label class="mb-0 form-label">Level of Education</label>
                                <input v-model.trim="education" type="text" class="form-control"
                                    placeholder="Enter level" />
                            </div>
                        </div>
                        <div class="col-6">
                            <div class="form-group mb-3">
                                <label class="mb-0 form-label">Occupation</label>
                                <input v-model.trim="occupation" type="text" class="form-control"
                                    placeholder="Enter occupation" />
                            </div>
                        </div>
                    </div>
                    <div class="form-group mb-3">
                        <label class="mb-0 form-label">Siblings</label>
                        <input v-model.trim="siblings" type="text" class="form-control" placeholder="Enter siblings" />
                    </div>
                    <div class="form-group mb-0">
                        <label class="mb-0 form-label">Referred By</label>
                        <input v-model.trim="referredBy" type="text" class="form-control"
                            placeholder="Referral source" />
                    </div>
                </div>
            </div>
        </form>

        <template #modal-footer>
            <div v-if="error" class="alert alert-danger mb-0" style="flex: 1; margin-right: 1rem;">{{ error }}</div>
            <b-button variant="outline-secondary" :disabled="isSubmitting" @click="hideModal">
                Cancel
            </b-button>
            <b-button type="submit" variant="primary" :disabled="isSubmitting" @click="submitForm">
                {{ isSubmitting ? 'Saving...' : 'Update Patient' }}
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import axios from "axios";
import Multiselect from "vue-multiselect";
import { countriesMethods, countriesComputed } from "@/state/helpers";

export default {
    components: {
        Multiselect,
    },
    props: {
        patientId: {
            type: [String, Number],
            default: null,
        },
    },
    data() {
        return {
            showModal: false,
            isSubmitting: false,
            error: "",
            fullName: "",
            gender: null,
            age: null,
            maritalStatus: "",
            address: "",
            nationality: { name: "Uganda" },
            residential_status: "",
            education: "",
            occupation: "",
            siblings: '',
            siblingInput: "",
            referredBy: "",
            nok: "",
            nok_contact: "",
            useAgeOrDob: "age",
            dateOfBirth: null,
            residentialStatusMap: {
                "Resident": 1,
                "Internally Displaced": 2,
                "Refugee": 3,
                "Migrant": 4,
                1: "Resident",
                2: "Internally Displaced",
                3: "Refugee",
                4: "Migrant",
            },
        };
    },
    computed: {
        ...countriesComputed,
    },
    mounted() {
        this.fetchCountries();
    },
    methods: {
        ...countriesMethods,
        show() {
            this.showModal = true;
            this.loadPatientData();
        },
        hideModal() {
            this.showModal = false;
        },
        submitForm() {
            this.$refs.form?.dispatchEvent(new Event('submit', { cancelable: true }));
        },
        async loadPatientData() {
            if (this.patientId) {
                try {
                    const cachedPatient = sessionStorage.getItem("psychoPatientBio");
                    if (cachedPatient) {
                        const parsed = JSON.parse(cachedPatient);
                        if (parsed && parsed.id && String(parsed.id) === String(this.patientId)) {
                            this.populateForm(parsed);
                            return;
                        }
                    }
                    const response = await axios.get(`/psycho-patient-bio/${this.patientId}`);
                    const patientData = response.data.psychopatients || response.data.psychopatient || response.data.patient;
                    if (patientData) {
                        const patient = Array.isArray(patientData) ? patientData[0] : patientData;
                        this.populateForm(patient);
                    }
                } catch (error) {
                    alert("Failed to load patient data:", error);
                }
            }
        },
        populateForm(patient) {
            if (!patient) return;
            this.fullName = patient.full_name || "";
            // Use gender_id directly from backend
            this.gender = patient.gender_id || null;
            this.age = Number.isFinite(Number(patient.age)) ? Number(patient.age) : null;
            this.maritalStatus = patient.marital_status || "";
            this.address = patient.address || "";
            this.nationality = patient.nationality ? { name: patient.nationality } : { name: "Uganda" };
            // Use residential_status_id directly from backend
            const residentialId = patient.residential_status_id;
            this.residential_status = residentialId ? this.residentialStatusMap[residentialId] : "";
            this.education = patient.education || "";
            this.occupation = patient.occupation || "";
            this.siblings = patient.siblings || "";
            this.referredBy = patient.referred_by || "";
            this.nok = patient.nok || "";
            this.nok_contact = patient.nok_contact || "";
            this.dateOfBirth = patient.date_of_birth ? String(patient.date_of_birth).split("T")[0] : null;
            this.useAgeOrDob = this.age !== null ? "age" : "dob";
        },
        convertResidentialStatus() {
            // This ensures the value stays as string for display
            // Conversion to number happens on submit
        },
        validateForm() {
            if (!this.fullName) {
                this.error = "Patient full name is required.";
                return false;
            }
            if (!this.gender) {
                this.error = "Please select the patient's gender.";
                return false;
            }
            if (this.useAgeOrDob === "age") {
                if (this.age === null || this.age === "" || Number(this.age) < 0) {
                    this.error = "Please provide a valid age.";
                    return false;
                }
            } else if (!this.dateOfBirth) {
                this.error = "Please provide a valid date of birth.";
                return false;
            }
            return true;
        },
        resetForm() {
            this.fullName = "";
            this.gender = null;
            this.age = null;
            this.maritalStatus = "";
            this.address = "";
            this.residential_status = "";
            this.education = "";
            this.occupation = "";
            this.siblings = '';
            this.siblingInput = "";
            this.referredBy = "";
            this.nok = "";
            this.nok_contact = "";
            this.useAgeOrDob = "age";
            this.dateOfBirth = null;
            this.error = "";
        },
        async updatePatient() {
            this.error = "";
            if (!this.validateForm()) return;

            this.isSubmitting = true;
            try {
                const payload = new FormData();
                payload.append("id", this.patientId);
                payload.append("full_name", this.fullName);
                payload.append("gender", this.gender);
                if (this.useAgeOrDob === "age") {
                    let dob = new Date();
                    dob.setFullYear(dob.getFullYear() - this.age);
                    payload.append("date_of_birth", dob.toISOString().split("T")[0]);
                } else {
                    payload.append("date_of_birth", this.dateOfBirth ?? "");
                }
                payload.append("marital_status", this.maritalStatus);
                payload.append("address", this.address);
                payload.append("nationality", this.nationality && this.nationality.name ? this.nationality.name : "");
                // Convert residential_status label back to numeric value
                const residentialValue = this.residentialStatusMap[this.residential_status] || this.residential_status || "";
                payload.append("residential_status", residentialValue);
                payload.append("education", this.education);
                payload.append("occupation", this.occupation);
                payload.append("siblings", this.siblings);
                payload.append("referred_by", this.referredBy);
                payload.append("nok", this.nok ?? "");
                payload.append("nok_contact", this.nok_contact ?? "");

                const response = await axios.post('/update-psycho-patient-bio', payload);
                if (response.status != 200) {
                    throw new Error(response.data.message || "Failed to update psychology patient.");
                }
                this.hideModal();
                this.$emit('patient-updated', response.data);
            } catch (e) {
                const apiMessage = e?.response?.data?.message;
                this.error = apiMessage || "Failed to update psychology patient.";
            } finally {
                this.isSubmitting = false;
            }
        },
    },
};
</script>

<style scoped>

.chip-text {
    font-weight: 500;
}

.chip-remove {
    background: none;
    border: none;
    color: #556ee6;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    line-height: 1;
    transition: color 0.2s ease;
}

.chip-remove:hover {
    color: #3f4ec7;
}

.siblings-input {
    flex: 1 1 150px;
    border: none !important;
    box-shadow: none !important;
    padding: 0.375rem 0.5rem !important;
    min-height: auto;
}

.siblings-input:focus {
    border: none !important;
    box-shadow: none !important;
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateX(-10px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}
</style>
